import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {A360ProductCd} from 'Quoting/models/Quoting';
import {VALUE_NOT_AVAILABLE} from 'Quoting/utils/utilities';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FormRowGrid, GridRowProps} from '../../../../shared/GridRow';
import {QuoteFormElement} from '../../../../shared/QuoteFormElement';
import {FormWrapper} from '../../../../shared/QuoteFormWrapper';
import {getCoverageDefinitionPropertyPaths} from './PropertyPaths';

export const CoverageDefinitionsBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan, selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);
  const productCd = selectedProduct?.code ?? '';
  const isEnabledPaymentStructure = (planCd: string) => planCd === 'GIM2';

  const selectedPlanCd =
    getQuoteFieldValue<string>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}].planCd`) ??
    VALUE_NOT_AVAILABLE;

  const terminationAgeSearchKey = 
   productCd === A360ProductCd.ACC
      ? `${selectedProduct?.code ?? ''}CoverageDefinition.terminationAge`
      : `${selectedProduct?.code ?? ''}CoverageDefinition.terminationAgeCd`;


  const planStructureSearchKey =
    productCd === A360ProductCd.ACC
      ? `${selectedProduct?.code ?? ''}CoverageDefinition.coverageBasisCd`
      : `${selectedProduct?.code ?? ''}CoverageDefinition.planStructure`;

  const getDependentChildLabel =
    productCd === 'ACC' ? 'Each Dependent Child' : 'Each Dependent Child (Excluding Newborn)';

  const propertyPaths = getCoverageDefinitionPropertyPaths(selectedPlan?.planIndex ?? 0, 0, selectedProduct?.code);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'coverage-definitions-row-1'},
      items: [
        {
          key: propertyPaths.whoCoveredCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${selectedProduct?.code ?? ''}CoverageDefinition.whoCoveredCd`}
              label="Who is Covered?"
              value={getQuoteFieldValue(propertyPaths.whoCoveredCd)}
              id={propertyPaths.whoCoveredCd}
              onChange={e => updateQuoteFieldValue(propertyPaths.whoCoveredCd, e.target.value as string)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 6, lg: 6},
        },
        {
          key: propertyPaths.benefitAmountSpouseBenefitPct,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${selectedProduct?.code ?? ''}BenefitAmountSpouse.benefitPct`}
              label="Spouse"
              value={getQuoteFieldValue(propertyPaths.benefitAmountSpouseBenefitPct)}
              id={propertyPaths.benefitAmountSpouseBenefitPct}
              onChange={e =>
                updateQuoteFieldValue(propertyPaths.benefitAmountSpouseBenefitPct, e.target.value as string)
              }
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 6, lg: 6},
        },
        {
          key: propertyPaths.benefitAmountChildBenefitPct,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${selectedProduct?.code ?? ''}BenefitAmountChild.benefitPct`}
              label={getDependentChildLabel}
              value={getQuoteFieldValue(propertyPaths.benefitAmountChildBenefitPct)}
              id={propertyPaths.benefitAmountChildBenefitPct}
              onChange={e =>
                updateQuoteFieldValue(propertyPaths.benefitAmountChildBenefitPct, e.target.value as string)
              }
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.terminationAge,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={terminationAgeSearchKey}
              label="Termination Age"
              value={getQuoteFieldValue(propertyPaths.terminationAge)}
              id={propertyPaths.terminationAge}
              onChange={e => updateQuoteFieldValue(propertyPaths.terminationAge, e.target.value as string)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.planStructure,
          hidden: isEnabledPaymentStructure(selectedPlanCd),
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={planStructureSearchKey}
              label="Plan Structure"
              value={getQuoteFieldValue(propertyPaths.planStructure)}
              id={propertyPaths.planStructure}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.rateGuaranteeMonths,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Rate Guarantee (Months)"
              id={propertyPaths.rateGuaranteeMonths}
              value={getQuoteFieldValue(propertyPaths.rateGuaranteeMonths)}
              onChange={e => updateQuoteFieldValue(propertyPaths.rateGuaranteeMonths, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.benefitPayment,
          hidden: selectedPlanCd !== 'GHI3',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${selectedProduct?.code ?? ''}CoverageDefinition.benefitPayment`}
              label="Benefit Payment Type"
              id={propertyPaths.benefitPayment}
              value={getQuoteFieldValue(propertyPaths.benefitPayment)}
              onChange={e => updateQuoteFieldValue(propertyPaths.benefitPayment, e.target.value as string)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Coverage Definition" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
